import React from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { ensureUser } from '../../util/data';
import { Avatar, UserDisplayName } from '../../components';

import css from './ReviewResponse.module.css';

/**
 * A component that displays a provider's response to a review.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Object} props.provider - The provider who wrote the response
 * @param {Object} props.response - The response data { text, date }
 * @returns {JSX.Element}
 */
const ReviewResponse = props => {
  const intl = useIntl();
  const { className, rootClassName, provider, response } = props;
  const classes = classNames(rootClassName || css.root, className);

  const safeProvider = ensureUser(provider);
  const { text, date } = response || {};

  if (!text) {
    return null;
  }

  const responseDate = new Date(date);
  const dateString = intl.formatDate(responseDate, { month: 'long', year: 'numeric' });

  const headerMessage = intl.formatMessage(
    { id: 'ReviewResponse.header' },
    { name: <UserDisplayName user={safeProvider} intl={intl} /> }
  );

  return (
    <div className={classes}>
      <div className={css.responseHeader}>
        <Avatar className={css.avatar} user={safeProvider} />
        <div className={css.responseInfo}>
          <div className={css.headerText}>{headerMessage}</div>
          <div className={css.responseDate}>{dateString}</div>
        </div>
      </div>
      <div className={css.responseContent}>
        {text}
      </div>
    </div>
  );
};

export default ReviewResponse;